"use client";

// Client-side PostHog provider. Initialises the SDK once on first mount,
// identifies the user when their distinctId becomes available, and resets
// on signout. Privacy posture:
//   - EU cloud (eu.i.posthog.com) — data residency in EU
//   - autocapture ON: captures clicks/forms but NOT input values by default
//   - mask_all_text/element_value: false (we want clickable text labels for
//     funnel analysis), but NEVER captures `<input>` / `<textarea>` content
//   - respect_dnt: true (honors Do-Not-Track)
//   - session replay ON with all text + inputs masked — captures
//     interaction/layout/navigation, never note or inspection content
//   - capture_pageview: true (default — autotagged with $pathname)
//
// To opt out a specific element from autocapture, add `class="ph-no-capture"`.

import { useEffect } from "react";
import posthog, { type CaptureResult } from "posthog-js";

interface IdentifyShape {
  userId: string;
  email?: string | null;
  isAdmin?: boolean;
}

function readIdentityFromMeta(): IdentifyShape | null {
  if (typeof document === "undefined") return null;
  const userId = document
    .querySelector('meta[name="posthog-user-id"]')
    ?.getAttribute("content");
  if (!userId) return null;
  const email =
    document.querySelector('meta[name="posthog-user-email"]')?.getAttribute("content") ??
    undefined;
  const isAdmin =
    document
      .querySelector('meta[name="posthog-user-admin"]')
      ?.getAttribute("content") === "true";
  return { userId, email, isAdmin };
}

// Drop Next.js's synthetic control-flow throws (`redirect()` and `notFound()`
// each throw an Error whose message is the literal string below to drive
// navigation). Auto exception capture grabs them on the client before Next's
// router reducer consumes them — they are not real errors.
//
// posthog-js puts the exceptions on `$exception_list` — an array of
// `{ type, value, ... }` entries where `value` is the error message. (The
// flat `$exception_values` array is derived server-side during ingestion, so
// it is NOT present here, at `before_send` time.)
//
// Exported for unit testing — the live config wires this into `before_send`.
export function filterPostHogEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event) return event;
  if (event.event !== "$exception") return event;
  const list = event.properties?.$exception_list;
  if (
    Array.isArray(list) &&
    list.some((entry) => {
      const value = (entry as { value?: unknown } | null)?.value;
      return value === "NEXT_REDIRECT" || value === "NEXT_NOT_FOUND";
    })
  ) {
    return null;
  }
  return event;
}

let initialised = false;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (initialised) return;
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://eu.i.posthog.com";
    if (!key) {
      // No key locally is fine — analytics simply doesn't fire.
      return;
    }
    posthog.init(key, {
      api_host: host,
      person_profiles: "identified_only",
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      respect_dnt: true,
      // Session replay ON. All text and input values are masked, so
      // recordings capture interaction, layout, and navigation but never
      // note, inspection, or queen content. Honors Do-Not-Track.
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "*",
      },
      // Auto-capture uncaught exceptions + unhandled promise rejections from
      // the browser into PostHog's Error Tracking. Pairs with the server-side
      // captureException helper in src/lib/analytics.ts. Counted separately
      // from product analytics in PostHog's billing (free up to 100k/month).
      capture_exceptions: true,
      // Never send the value of <input>/<textarea> elements via autocapture.
      // PostHog's default already excludes most input values; this is belt-
      // and-braces for the few exceptions (e.g. type="text" without sensitive
      // attribute).
      mask_all_text: false,
      mask_all_element_attributes: false,
      before_send: filterPostHogEvent,
    });
    initialised = true;
  }, []);

  // Identify whenever the server-rendered user identity changes. We read
  // it from `<meta>` tags injected by the root layout — this avoids
  // shipping the user id through React props/state and keeps the provider
  // simple.
  useEffect(() => {
    if (!initialised) return;
    const id = readIdentityFromMeta();
    if (id?.userId) {
      posthog.identify(id.userId, {
        email: id.email ?? undefined,
        is_admin: id.isAdmin ?? false,
        // PostHog's "Internal / Test users" cohort keys on this, which is
        // what the project's filter-test-accounts toggle excludes.
        $internal_or_test_user: id.isAdmin ?? false,
      });
    } else {
      // Signed out — drop the previous identity so subsequent events
      // are anonymous, not attributed to the prior user.
      posthog.reset();
    }
  });

  return <>{children}</>;
}
